import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useMediaQuery, Dialog, Slide } from '@mui/material';
import { Clear } from '@mui/icons-material';
import { useAccount } from './../../../contexts/Account';
import {
  getAdvisorPlanUpgrades,
  getAdvisorRecurlyPlan,
} from './../../../services/advisor_services';
import eventBus, { eventBusValues } from './../../../eventBus';
import Step1 from './Step1';
import Step2 from './Step2';
import { sendFSEventUpgradeViewed } from '../../../services/FullStory';

const AdvisorFreemiumModal = ({ onUpgradeSuccess }) => {
  const {
    accountDetails,
    currentPlan,
    asLoggedUserDetails,
    currentUserInfo,
    type,
  } = useAccount();
  const isSM = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const isXS = useMediaQuery((theme) => theme.breakpoints.down(780));
  const [openModal, setOpenModal] = useState(false);
  const [step1, setStep1] = useState(true);
  const [monthlyUpgrade1, setMonthlyUpgrade1] = useState('');
  const [monthlyUpgrade2, setMonthlyUpgrade2] = useState('');
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [planData, setPlanData] = useState({});
  const [fromBanner, setFromBanner] = useState(false);
  const [origin, setOrigin] = useState('');
  const [isSingleOption, setIsSingleOption] = useState(true);
  const location = useLocation();

  const handleContinue = () => {
    setStep1(false);
  };
  const handleBack = () => {
    setStep1(true);
  };

  useEffect(() => {
    async function fetchData() {
      const planResponses = await getAdvisorRecurlyPlan({
        recurlyPlanCode: accountDetails.recurlyPlanCode,
      });

      const { b2cFreemiumMonthlyUpgrade1, b2cFreemiumMonthlyUpgrade2 } =
        planResponses.data.getRecurlyPlan;

      const planResponsesMonth1 = await getAdvisorPlanUpgrades({
        RECURLY_PLAN_CODE: b2cFreemiumMonthlyUpgrade1,
      });
      setMonthlyUpgrade1(planResponsesMonth1.data.getFreeTrialUpgrades[0]);

      if (b2cFreemiumMonthlyUpgrade2) {
        const planResponsesMonth2 = await getAdvisorPlanUpgrades({
          RECURLY_PLAN_CODE: b2cFreemiumMonthlyUpgrade2,
        });
        setMonthlyUpgrade2(planResponsesMonth2.data.getFreeTrialUpgrades[0]);
      } else {
        setPlanData({
          option: 1,
          data: planResponsesMonth1.data.getFreeTrialUpgrades[0],
        });
        setStep1(false);
        setIsFirstLoad(false);
      }
    }

    eventBus.on(eventBusValues.triggerAdvisorUpgradeAction, (data) => {
      setFromBanner(data ? data.fromBanner : false);
      setOpenModal(true);
      setOrigin(data ? data.origin : '');
    });

    fetchData();
    return () => {
      eventBus.remove(eventBusValues.triggerAdvisorUpgradeAction);
    };
  }, []);

  useEffect(() => {
    const upgrade1Origins = [
      'free-trial-banner',
      'advisor-profile-banner',
      'profile-advisory-rate-gates',
      'get-advisor-rate-suggestions',
      'activate-profile',
      'join-unlimited-boards',
      'search-advisory-boards',
      'directory-button',
      'board-join-button',
      'accept-invitation',
      'url-upgrade1',
      // 'submit-idea',
      // 'add-to-resume',
      // 'post-answer-response',
      // 'board-meeting-ask-question',
    ];

    const upgrade2Origins = ['get-vip-access', 'url-upgrade2'];

    const mainPageOrigins = [
      'see-all-upgrade-options',
      'view-membership-options',
      'url-main',
    ];
    if (upgrade1Origins.includes(origin)) {
      setPlanData({
        option: 1,
        data: monthlyUpgrade1,
      });
      setStep1(false);
      setIsFirstLoad(false);
      setIsSingleOption(true);
    } else if (upgrade2Origins.includes(origin)) {
      setPlanData({
        option: 2,
        data: monthlyUpgrade2,
      });
      setStep1(false);
      setIsFirstLoad(false);
      setIsSingleOption(true);
    } else if (mainPageOrigins.includes(origin)) {
      setStep1(true);
      setIsFirstLoad(true);
      setIsSingleOption(false);
    } else {
      setPlanData({
        option: 1,
        data: monthlyUpgrade1,
      });
      setStep1(false);
      setIsFirstLoad(false);
      setIsSingleOption(true);
    }
  }, [origin, openModal]);

  function handleClose() {
    setOpenModal(false);
    if (monthlyUpgrade2) {
      setStep1(true);
    }
  }

  const sendFSViewedEvent = (upgradeAttempted = false) => {
    sendFSEventUpgradeViewed({
      accountDetails,
      currentPlan: currentPlan,
      asLoggedUserDetails,
      currentUserInfo,
      source: fromBanner ? 'banner' : 'gate',
      type,
      upgradeType: 'freemium',
      upgradeAttempted,
    });
  };

  const handleOptionChange = (option, data) => {
    setPlanData({ option, data });
  };

  const handlePlanToggle = (selectedPlan) => {
    setPlanData((currentPlanData) => {
      if (selectedPlan === 'annual' && currentPlanData.plan === 'month') {
        return {
          ...currentPlanData,
          plan: 'annual',
          data: currentPlanData.counterpartData,
          counterpartData: currentPlanData.data,
        };
      } else if (
        selectedPlan === 'month' &&
        currentPlanData.plan === 'annual'
      ) {
        return {
          ...currentPlanData,
          plan: 'month',
          data: currentPlanData.counterpartData,
          counterpartData: currentPlanData.data,
        };
      }
      return currentPlanData;
    });
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('b2c_freemium_upgrade_popup') === 'upgrade1') {
      setOpenModal(true);
      setOrigin('url-upgrade1');
      setPlanData({
        option: 1,
        data: monthlyUpgrade1,
      });
    } else if (params.get('b2c_freemium_upgrade_popup') === 'upgrade2') {
      setOpenModal(true);
      setOrigin('url-upgrade2');
      setPlanData({
        option: 2,
        data: monthlyUpgrade2,
      });
    } else if (params.get('b2c_freemium_upgrade_popup') === 'main') {
      setOpenModal(true);
      setOrigin('url-main');
    }
  }, [location.search, monthlyUpgrade1, monthlyUpgrade2]);

  return (
    <div>
      <Dialog
        open={openModal}
        fullScreen={isXS}
        scroll="paper"
        className="not-scrollable"
        onClose={() => {
          handleClose();
          sendFSViewedEvent();
        }}
        PaperProps={{
          id: 'advisorFreemiumUpgradeModal',
          sx: {
            borderRadius: isXS ? '0px' : '15px',
            boxShadow: '0 5px 20px rgba(0, 0, 0, 0.07)',
            height: isXS ? '100%' : '530px',
            width: isXS ? '100%' : '920px',
          },
        }}
      >
        {isSM && (
          <div
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              cursor: 'pointer',
            }}
          >
            <Clear
              onClick={() => {
                handleClose();
                sendFSViewedEvent();
              }}
            />
          </div>
        )}

        {isFirstLoad ? (
          <div>
            <Step1
              onContinue={() => {
                handleContinue();
                setIsFirstLoad(false);
              }}
              month1={monthlyUpgrade1}
              month2={monthlyUpgrade2}
              onOptionChange={handleOptionChange}
            />
          </div>
        ) : (
          <Slide direction="right" in={step1} mountOnEnter unmountOnExit>
            <div>
              <Step1
                onContinue={handleContinue}
                month1={monthlyUpgrade1}
                month2={monthlyUpgrade2}
                onOptionChange={handleOptionChange}
              />
            </div>
          </Slide>
        )}
        <Slide
          direction="left"
          in={!step1}
          mountOnEnter
          unmountOnExit
          timeout={800}
        >
          <div>
            <Step2
              planData={planData}
              onPlanChange={handlePlanToggle}
              goBack={handleBack}
              closeModal={handleClose}
              onUpgradeSuccess={onUpgradeSuccess}
              fromBanner={fromBanner}
              isSingleOption={monthlyUpgrade2 && !isSingleOption ? false : true}
            />
          </div>
        </Slide>
      </Dialog>
    </div>
  );
};

export default AdvisorFreemiumModal;
